import { useEffect, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import type { BannerTrigger } from "@/content/dashboardBanners";

/** On a phone, this long without a touch counts as about to leave. */
export const INACTIVITY_MS = 30_000;
/** On a phone, scrolling up this far in one go counts as about to leave. */
export const SCROLL_UP_PX = 80;
/** A finger moving less than this is a tap, not a drag. */
const TAP_SLOP_PX = 10;
/** After the finger lifts, a fling keeps the page scrolling about this long. */
export const FLING_MS = 1_000;

const ACTIVITY_EVENTS = [
  "touchstart",
  "touchmove",
  "scroll",
  "pointerdown",
  "keydown",
] as const;

type Triggered = { id: string; trigger: BannerTrigger } | null;

/**
 * Whether the banner's trigger has fired: on entry after its delay, or when
 * the reader is about to leave, which on desktop is the pointer leaving the
 * page at the top and on a phone a while without a touch or a scroll up. The
 * delay runs on while the banner stays picked from page to page. Once another
 * banner or none is picked, what fired is forgotten, so a banner picked again
 * waits for its trigger again.
 */
export const useBannerTrigger = (banner: Triggered) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [firedFor, setFiredFor] = useState<string | null>(null);
  const id = banner?.id ?? null;
  const kind = banner?.trigger.kind;
  const afterSeconds =
    banner?.trigger.kind === "entry" ? banner.trigger.afterSeconds : 0;
  // Only exit intent watches differently on a phone. MUI's media query answers
  // false on the first render, so depending on it alone would restart an
  // entry delay on every phone.
  const leavingOnPhone = kind === "exitIntent" && isMobile;

  useEffect(() => {
    // A banner set to Off is never picked; were one handed over, it never fires.
    if (!id || kind === "off") return;
    const fire = () => setFiredFor(id);
    const stop =
      kind === "entry"
        ? waitFor(fire, afterSeconds)
        : leavingOnPhone
          ? watchLeavingOnPhone(fire)
          : watchLeavingOnDesktop(fire);
    return () => {
      stop();
      setFiredFor(null);
    };
  }, [id, kind, afterSeconds, leavingOnPhone]);

  return id !== null && firedFor === id;
};

const waitFor = (fire: () => void, seconds: number) => {
  const timer = setTimeout(fire, seconds * 1000);
  return () => clearTimeout(timer);
};

/** The pointer leaving the page through its top edge, for the tabs or address bar. */
const watchLeavingOnDesktop = (fire: () => void) => {
  const onLeave = (event: MouseEvent) => {
    if (event.clientY <= 0) fire();
  };
  document.addEventListener("mouseleave", onLeave);
  return () => document.removeEventListener("mouseleave", onLeave);
};

/** A while without a touch, or a scroll up of some length. */
const watchLeavingOnPhone = (fire: () => void) => {
  const stopIdle = watchIdle(fire);
  const stopScrollUp = watchScrollUp(fire);
  return () => {
    stopIdle();
    stopScrollUp();
  };
};

const watchIdle = (fire: () => void) => {
  let idle = setTimeout(fire, INACTIVITY_MS);
  const touched = () => {
    clearTimeout(idle);
    idle = setTimeout(fire, INACTIVITY_MS);
  };
  ACTIVITY_EVENTS.forEach((type) =>
    window.addEventListener(type, touched, { passive: true })
  );
  return () => {
    clearTimeout(idle);
    ACTIVITY_EVENTS.forEach((type) =>
      window.removeEventListener(type, touched)
    );
  };
};

/**
 * A scroll up the reader made with their finger, or with the fling it set off.
 * The page scrolls itself too, as the router does to the top of the next page
 * or back to where Back returns, so a scroll with no finger behind it is not
 * counted; nor is one right after a tap, which is not a drag.
 */
const watchScrollUp = (fire: () => void) => {
  let lastY = window.scrollY;
  let scrolledUp = 0;
  let touchY = 0;
  let draggedAt = -Infinity;
  const onTouchStart = (event: TouchEvent) => {
    touchY = event.touches[0]?.clientY ?? 0;
    draggedAt = -Infinity;
  };
  const onTouchMove = (event: TouchEvent) => {
    const y = event.touches[0]?.clientY ?? touchY;
    if (Math.abs(y - touchY) > TAP_SLOP_PX) draggedAt = Date.now();
  };
  const onScroll = () => {
    const y = window.scrollY;
    const byFinger = Date.now() - draggedAt < FLING_MS;
    scrolledUp = byFinger && y < lastY ? scrolledUp + (lastY - y) : 0;
    lastY = y;
    if (scrolledUp >= SCROLL_UP_PX) fire();
  };
  const options = { passive: true } as const;
  window.addEventListener("touchstart", onTouchStart, options);
  window.addEventListener("touchmove", onTouchMove, options);
  window.addEventListener("scroll", onScroll, options);
  return () => {
    window.removeEventListener("touchstart", onTouchStart);
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("scroll", onScroll);
  };
};
